/**
 * Расчёты окна товара: проверки, вывод «Что дальше», бейджи шапки.
 *
 * Чистые функции без React: правила «что считать проблемой» проверяются
 * тестами отдельно от разметки. Принцип один — не утверждать лишнего: чего
 * нет в данных, то «нет данных» с причиной, а не зелёная галочка и не ошибка.
 */

import type { BadgeVariant } from '../../../../components/ui/Badge'
import type { NextStepCheck, NextStepTone } from '../../../../components/ui/NextStepBar'
import { formatPrice, isLinkedToOzon, isLinkedToWb, printArtifactInfo } from '../../lib'
import { livePlatformCount, PRESENCE_MARKETPLACES } from '../../presence'
import type { ProductPresenceEntry, ProductV2 } from '../../types'

export const NO_DATA = 'нет данных'

/** «1 замечание», «2 замечания», «5 замечаний». */
export function pluralRemarks(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return `${count} замечание`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} замечания`
  return `${count} замечаний`
}

export interface OneCInfo {
  label: string
  variant: BadgeVariant
  tone: NextStepCheck['tone']
  /** Короткое значение чипа. */
  value: string
}

/** Состояние синхронизации с 1С; null — статус не пришёл. */
export function oneCInfo(product: ProductV2): OneCInfo | null {
  const status = String(product.one_c_status ?? '').trim().toLowerCase()
  if (!status) return null
  if (status === 'synced') return { label: 'В 1С', variant: 'green', tone: 'ok', value: 'синхронизирован' }
  if (status === 'pending_1c') return { label: 'Ждёт 1С', variant: 'yellow', tone: 'warn', value: 'ждёт' }
  if (status === 'error' || status === 'failed' || status === 'sync_error') {
    return { label: 'Ошибка 1С', variant: 'red', tone: 'bad', value: 'ошибка' }
  }
  return { label: status, variant: 'gray', tone: 'none', value: status }
}

export const isManualProduct = (product: ProductV2 | null | undefined): boolean =>
  product?.design?.origin === 'external'

export const priceOf = (product: ProductV2): number | null => {
  const value = product.price ?? product.category?.price ?? null
  const number = Number(value)
  return value !== null && Number.isFinite(number) && number > 0 ? number : null
}

export const validationCount = (product: ProductV2): number | null => {
  if (product.validation_status === 'has_issues') {
    return Math.max(product.validation_issues?.length ?? 0, 1)
  }
  return product.validation_status === 'ready' ? 0 : null
}

/** Номер тематики макета, если бэкенд отдал его в данных макета. */
export function themeNumberOfProduct(product: ProductV2): number | null {
  const raw = (product.design as { theme_number?: unknown } | undefined)?.theme_number
  if (raw === null || raw === undefined || raw === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

export const productImages = (product: ProductV2): string[] =>
  (product.image_urls ?? []).filter((url) => typeof url === 'string' && url.trim() !== '')

export function productLink(article: string): string {
  const path = `/products?article=${encodeURIComponent(article)}`
  try {
    return new URL(path, window.location.origin).toString()
  } catch {
    return path
  }
}

/** Префикс и название категории: «PP · Пряники». */
export function categoryLabel(product: ProductV2, fallback?: string): string | null {
  const category = product.category
  const prefix = category?.prefix?.trim()
  const description = category?.description?.trim() || fallback?.trim()
  if (prefix && description) return `${prefix} · ${description}`
  return description || prefix || null
}

/** Что считать «внешним ID» для шапки: nmID ВБ, offer ОЗОН, SKU Маркета. */
export function marketplaceIds(product: ProductV2): {
  wb: string | null
  ozon: string | null
  ym: string | null
} {
  return {
    wb: isLinkedToWb(product) ? String(product.wb_nm_id) : null,
    ozon: isLinkedToOzon(product) ? String(product.ozon_offer_id ?? product.ozon_product_id) : null,
    ym: product.ym_market_sku ? String(product.ym_market_sku) : null,
  }
}

// ── Проверки полосы «Что дальше» ─────────────────────────────────────

export interface CheckHandlers {
  /** Клик по чипу ведёт на вкладку. */
  goTab: (tab: string) => void
}

/** Проверка + человеческая формулировка проблемы (для вывода одной фразой). */
export interface ProductCheck extends NextStepCheck {
  problem?: string
}

export function oneCCheck(product: ProductV2, { goTab }: CheckHandlers): ProductCheck {
  const info = oneCInfo(product)
  if (!info) {
    return { key: 'onec', label: '1С', tone: 'none', title: 'статус синхронизации не получен', onClick: () => goTab('attrs') }
  }
  return {
    key: 'onec',
    label: '1С',
    value: info.value,
    tone: info.tone,
    onClick: () => goTab('attrs'),
    problem:
      info.tone === 'bad'
        ? 'Ошибка синхронизации с 1С'
        : info.tone === 'warn'
          ? 'Товар ещё не передан в 1С'
          : undefined,
  }
}

export function printCheck(product: ProductV2, { goTab }: CheckHandlers): ProductCheck {
  const artifact = product.print_artifact
  const go = () => goTab('production')
  if (artifact?.status === 'ready') {
    const info = printArtifactInfo(product)
    return info.variant === 'green'
      ? { key: 'print', label: 'Файл печати', value: 'готов', tone: 'ok', onClick: go }
      : { key: 'print', label: 'Файл печати', value: 'нет файла', tone: 'bad', onClick: go, problem: info.reason || 'У товара нет файла печати' }
  }
  if (artifact?.status === 'failed') {
    return {
      key: 'print',
      label: 'Файл печати',
      value: 'ошибка',
      tone: 'bad',
      onClick: go,
      problem: 'Файл печати не создан: ошибка генерации',
    }
  }
  if (artifact?.status === 'generating' || artifact?.status === 'pending') {
    return { key: 'print', label: 'Файл печати', value: 'создаётся', tone: 'info', onClick: go }
  }
  if (isManualProduct(product)) {
    return {
      key: 'print',
      label: 'Файл печати',
      tone: 'none',
      title: 'товар без макета: файл печати не предусмотрен',
      onClick: go,
    }
  }
  return {
    key: 'print',
    label: 'Файл печати',
    value: 'нет',
    tone: 'bad',
    onClick: go,
    problem: 'У товара нет файла печати',
  }
}

export function visualsCheck(product: ProductV2, { goTab }: CheckHandlers): ProductCheck {
  const count = productImages(product).length
  if (count > 0) {
    return { key: 'visuals', label: 'Визуалы', value: count, tone: 'ok', onClick: () => goTab('media') }
  }
  return {
    key: 'visuals',
    label: 'Визуалы',
    tone: 'none',
    title: 'собственных изображений товара нет; кадры воронок по кабинетам в данных товара не приходят',
    onClick: () => goTab('media'),
  }
}

export function placesCheck(
  entries: ProductPresenceEntry[] | null,
  presenceError: string | null,
  { goTab }: CheckHandlers,
): ProductCheck {
  const go = () => goTab('places')
  if (entries === null) {
    return {
      key: 'places',
      label: 'Площадки',
      tone: 'none',
      title: presenceError ? `присутствие не загрузилось (${presenceError})` : 'присутствие на площадках загружается',
      onClick: go,
    }
  }
  const total = PRESENCE_MARKETPLACES.length
  const live = livePlatformCount(entries) ?? 0
  const failed = entries.filter((entry) => entry.state === 'error' || entry.channel_state === 'error' || (entry.errors?.length ?? 0) > 0)
  const labels: Record<string, string> = { wb: 'Wildberries', ozon: 'OZON', ym: 'Яндекс Маркет', site: 'Сайт' }
  const failedNames = [...new Set(failed.map((entry) => labels[entry.marketplace] || entry.marketplace))].join(', ')
  const pending = entries.some((entry) => entry.state === 'draft' || entry.state === 'publishing')
  if (failed.length > 0) {
    return {
      key: 'places',
      label: 'Площадки',
      value: `${live} из ${total}`,
      tone: 'bad',
      onClick: go,
      problem: failed.every((entry) => entry.state === 'live')
        ? failedNames + ': ошибка обновления карточки на витрине'
        : failedNames + ': ошибка отправки карточки',
      title: live > 0 ? 'Товар остаётся на витрине: ' + live + ' из ' + total + ' площадок' : undefined,
    }
  }
  if (live > 0) return { key: 'places', label: 'Площадки', value: `${live} из ${total}`, tone: 'ok', onClick: go }
  if (pending) {
    return {
      key: 'places',
      label: 'Площадки',
      value: `0 из ${total}`,
      tone: 'info',
      onClick: go,
      title: 'есть черновики, ждут выгрузки',
    }
  }
  return {
    key: 'places',
    label: 'Площадки',
    value: `0 из ${total}`,
    tone: 'warn',
    onClick: go,
    problem: 'Товара нет ни на одной площадке',
  }
}

export function validationCheck(product: ProductV2, { goTab }: CheckHandlers): ProductCheck {
  const count = validationCount(product)
  const go = () => goTab('attrs')
  if (count === null) {
    return { key: 'validation', label: 'Данные', tone: 'none', title: 'проверка данных товара не выполнялась', onClick: go }
  }
  if (count === 0) return { key: 'validation', label: 'Данные', value: 'без замечаний', tone: 'ok', onClick: go }
  return {
    key: 'validation',
    label: 'Данные',
    value: pluralRemarks(count),
    tone: 'warn',
    onClick: go,
    problem: `В данных товара ${pluralRemarks(count)}`,
  }
}

export function priceCheck(product: ProductV2, { goTab }: CheckHandlers): ProductCheck {
  const price = priceOf(product)
  const go = () => goTab('overview')
  if (price !== null) return { key: 'price', label: 'Цена', value: formatPrice(price), tone: 'ok', onClick: go }
  return {
    key: 'price',
    label: 'Цена',
    value: 'не задана',
    tone: 'bad',
    onClick: go,
    problem: 'У категории не задана цена',
  }
}

export interface ProductVerdict {
  tone: NextStepTone
  title: string
  hint?: string
  problemKey?: string
}

const SEVERITY: Record<NextStepCheck['tone'], number> = { bad: 4, warn: 3, info: 2, ok: 1, none: 0 }

/**
 * Вывод одной фразой: самая важная проблема из проверок. Остальные
 * проблемы — числом в подсказке, чипы под строкой показывают их все.
 */
export function buildVerdict(checks: ProductCheck[], loading: boolean): ProductVerdict {
  if (loading) return { tone: 'neutral', title: 'Проверяем данные товара' }
  const problems = checks
    .filter((check) => check.problem && (check.tone === 'bad' || check.tone === 'warn'))
    .sort((left, right) => SEVERITY[right.tone] - SEVERITY[left.tone])
  if (problems.length > 0) {
    const first = problems[0]
    return {
      tone: first.tone === 'bad' ? 'bad' : 'warn',
      title: first.problem as string,
      problemKey: first.key,
      hint: problems.length > 1 ? `Ещё проблем: ${problems.length - 1}. Все проверки — ниже.` : undefined,
    }
  }
  if (checks.some((check) => check.tone === 'info')) {
    const waiting = checks.find((check) => check.tone === 'info')
    return {
      tone: 'info',
      title: 'Часть операций ещё выполняется',
      hint: waiting?.title ? `${waiting.label}: ${waiting.title}.` : undefined,
    }
  }
  if (checks.some((check) => check.tone === 'none')) {
    return { tone: 'neutral', title: 'По доступным данным проблем не найдено', hint: 'Часть проверок недоступна. Подробности — в «Все проверки».' }
  }
  return { tone: 'ok', title: 'Товар в порядке', hint: 'Проверки пройдены, действий не требуется.' }
}
